import type { Um } from './units.js';
import type { Issue } from './model.js';
import type { ProjectDocument } from './document.js';
import type { ShelfLinkProperty } from './shelves.js';

/**
 * Dimension links between panels of assemblies and/or parametric cabinets.
 * Invariant: value(b) = value(a) + offset. Mode 'equal' always has offset 0;
 * mode 'offset' ("link and keep difference") stores the difference captured when the link was made.
 */
export type PanelLinkProperty = 'length' | 'width' | 'thickness';
export type CabinetLinkProperty = 'width' | 'height' | 'depth';

export type LinkEnd =
  | { kind: 'panel'; unitId: string; panelKey: string; property: PanelLinkProperty }
  | { kind: 'cabinet'; unitId: string; property: CabinetLinkProperty }
  | { kind: 'shelves'; unitId: string; setId: string; property: ShelfLinkProperty };

export interface DimensionLink {
  id: string;
  a: LinkEnd;
  b: LinkEnd;
  mode: 'equal' | 'offset';
  offset: Um;
}

export const LINK_PROPERTY_LABEL: Record<PanelLinkProperty | CabinetLinkProperty | ShelfLinkProperty, string> = {
  count: 'Broj polica',
  gap: 'Zazor',
  length: 'Dužina',
  width: 'Širina',
  thickness: 'Debljina',
  height: 'Visina',
  depth: 'Dubina',
};

export function sameEnd(x: LinkEnd, y: LinkEnd): boolean {
  return endKey(x) === endKey(y);
}

export function endKey(e: LinkEnd): string {
  if (e.kind === 'panel') return `p:${e.unitId}:${e.panelKey}:${e.property}`;
  if (e.kind === 'shelves') return `s:${e.unitId}:${e.setId}:${e.property}`;
  return `c:${e.unitId}:${e.property}`;
}

export function getEndValue(doc: ProjectDocument, e: LinkEnd): Um | undefined {
  if (e.kind === 'panel') return doc.assemblies.find((a) => a.id === e.unitId)?.panels.find((p) => p.key === e.panelKey)?.[e.property];
  if (e.kind === 'shelves') return doc.assemblies.find((a) => a.id === e.unitId)?.shelfSets?.find((s) => s.id === e.setId)?.[e.property];
  return doc.cabinets.find((c) => c.id === e.unitId)?.params[e.property];
}

function setEndValue(doc: ProjectDocument, e: LinkEnd, v: Um): boolean {
  if (e.kind === 'panel') {
    const p = doc.assemblies.find((a) => a.id === e.unitId)?.panels.find((x) => x.key === e.panelKey);
    if (!p) return false;
    p[e.property] = v;
    return true;
  }
  if (e.kind === 'shelves') {
    const s = doc.assemblies.find((a) => a.id === e.unitId)?.shelfSets?.find((x) => x.id === e.setId);
    if (!s) return false;
    s[e.property] = v;
    return true;
  }
  const c = doc.cabinets.find((x) => x.id === e.unitId);
  if (!c) return false;
  c.params[e.property] = v;
  return true;
}

/** Creates a link; 'offset' captures the current difference b − a. */
export function makeLink(doc: ProjectDocument, id: string, a: LinkEnd, b: LinkEnd, mode: 'equal' | 'offset'): DimensionLink {
  const va = getEndValue(doc, a);
  const vb = getEndValue(doc, b);
  if (va === undefined || vb === undefined) throw new Error('Element za link ne postoji.');
  return { id, a, b, mode, offset: mode === 'equal' ? 0 : vb - va };
}

/**
 * Propagates a changed value from `start` through all links (breadth first, each end set once).
 * Returns the ends that were changed. Values that would become ≤ 0 are still written so the
 * normal validation reports them — links never silently clamp a dimension.
 */
export function propagateLinks(doc: ProjectDocument, start: LinkEnd): LinkEnd[] {
  const changed: LinkEnd[] = [];
  const visited = new Set([endKey(start)]);
  const queue: LinkEnd[] = [start];
  while (queue.length) {
    const cur = queue.shift()!;
    const v = getEndValue(doc, cur);
    if (v === undefined) continue;
    for (const l of doc.links ?? []) {
      let other: LinkEnd | null = null;
      let target = 0;
      if (sameEnd(l.a, cur)) {
        other = l.b;
        target = v + l.offset;
      } else if (sameEnd(l.b, cur)) {
        other = l.a;
        target = v - l.offset;
      }
      if (!other || visited.has(endKey(other))) continue;
      visited.add(endKey(other));
      if (getEndValue(doc, other) !== target && setEndValue(doc, other, target)) changed.push(other);
      queue.push(other);
    }
  }
  return changed;
}

/**
 * Called after every edit: ends whose value changed between `before` and `after` drive their links.
 * If both ends of one link were changed by the same edit, nothing is overwritten and validation reports it.
 */
export function syncLinksAfterEdit(before: ProjectDocument, after: ProjectDocument): void {
  const links = after.links ?? [];
  if (!links.length) return;
  const ends = new Map<string, LinkEnd>();
  for (const l of links) for (const e of [l.a, l.b]) ends.set(endKey(e), e);
  const driven = [...ends.values()].filter((e) => getEndValue(before, e) !== getEndValue(after, e));
  const drivenKeys = new Set(driven.map(endKey));
  for (const e of driven) {
    // Skip ends whose every link partner was also edited explicitly (user intent wins).
    const partners = links.filter((l) => sameEnd(l.a, e) || sameEnd(l.b, e)).map((l) => (sameEnd(l.a, e) ? l.b : l.a));
    if (partners.length && partners.every((p) => drivenKeys.has(endKey(p)))) continue;
    propagateLinks(after, e);
  }
}

/** Removes links whose ends no longer exist (e.g. after deleting a panel). */
export function pruneLinks(doc: ProjectDocument): void {
  doc.links = (doc.links ?? []).filter((l) => getEndValue(doc, l.a) !== undefined && getEndValue(doc, l.b) !== undefined);
}

export function validateLinks(doc: ProjectDocument): Issue[] {
  const issues: Issue[] = [];
  for (const l of doc.links ?? []) {
    const va = getEndValue(doc, l.a);
    const vb = getEndValue(doc, l.b);
    if (va === undefined || vb === undefined) {
      issues.push({ severity: 'warning', code: 'link.orphaned', message: `Link ${l.id} pokazuje na element koji više ne postoji.` });
    } else if (vb !== va + l.offset) {
      issues.push({
        severity: 'warning',
        code: 'link.out-of-sync',
        message: `Link ${l.id}: vrijednosti se ne poklapaju (${va / 1000} / ${vb / 1000} mm, očekivana razlika ${l.offset / 1000} mm).`,
        cabinetId: l.b.unitId,
      });
    }
  }
  return issues;
}
